import { Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import { Environment, ENVIRONMENT } from '@netfoundry-ui/shared/model';
import {
  ApiService,
  BrandingModeService,
  ErrorHistoryService, FeatureService,
  LoggerService,
  MenuService,
  ValidateService,
  URLS
} from '@netfoundry-ui/shared/services';

import {from, lastValueFrom, of, ReplaySubject, Subscription, timeout, timer} from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import {delay, isEmpty, result} from 'lodash';
import { Auth0Service } from './auth0.service';
import { AuthorizationService } from './authorization.service';
import { AwsSplashScreenComponent } from './aws-splash-screen/aws-splash-screen.component';
import { IamService } from './iam.service';
import { LoginServiceType } from './login-service.type';
import {ZitiLoginService} from "./ziti-login-service";

// ACUL surfaces custom /authorize params to the login bundle only when prefixed `ext-`, and Auth0
// validates each ext- value against /^[-\w.*~@+ /:]{1,255}$/ (no JSON/#/quotes/commas, <=255 chars).
// base64url fits that charset; use it for anything structured.
function toB64Url(value: string): string {
    return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

@Injectable({ providedIn: 'root' })
export class LoginService implements LoginServiceType {
    refreshSub = new Subscription();
    jwtHelper: JwtHelperService;
    dialogRef: MatDialogRef<any>;
    isAwsLogin = false;
    identityProviders = [];
    loginOrganization: any = {};
    options = {
        closable: true,
        autoclose: true,
        allowSignUp: false,
        allowLogin: true,
        auth: {
            redirectUrl: this.environment.authconfig.callbackURL,
            responseType: 'token id_token',
            audience: this.environment.authconfig.audience,
            params: {
                scope: 'openid email profile',
            },
        },
        languageDictionary: {
            title: 'NetFoundry',
        },
        theme: {
            logo: 'https://console.netfoundry.io/assets/icons/android-icon-192x192.png',
            primaryColor: '#006cff',
        },
        prefill: {
            email: '',
            username: '',
            firstName: '',
            lastName: '',
        },
    };
    isRootDomain = true;
    isRealmLogin = false;
    emailAddress = '';
    errorEmailAddress = false;
    forgotSiteName = false;
    isResuming = true;
    tenantLabel = '';
    errorSiteName = false;
    vanityLogin = false;
    showFailedLogin = false;
    isLoggedIn = false;
    loginProcessing = false;
    hasLoggedIn = false;
    // Set by logout() so the Auth0 error-observable subscription below doesn't
    // fire a "Session Expired" growler for errors that happen as a natural
    // consequence of the user-initiated logout (e.g. in-flight silent-refresh
    // calls that can't complete once we're logging out).
    private logoutInProgress = false;
    // Arms reload renewal once per session; isAuthenticated$ re-emits on every renewal.
    private renewalArmed = false;
    authenticationChangedSource = new ReplaySubject<boolean>();
    authenticationChanged = this.authenticationChangedSource.asObservable();

    currentRealm: any = {};

    logoutSource = new ReplaySubject<boolean>();
    logoutOb = this.logoutSource.asObservable();
    isVanityUrl = false;
    rootSubDomain = '';
    url = '';

    constructor(
        protected logger: LoggerService,
        private router: Router,
        private iamService: IamService,
        private activatedRoute: ActivatedRoute,
        private growlService: GrowlerService,
        private matDialog: MatDialog,
        private locationService: Location,
        private brandingService: BrandingModeService,
        private validateService: ValidateService,
        private authorizationService: AuthorizationService,
        private menuService: MenuService,
        private errorHistoryService: ErrorHistoryService,
        private dialogForm: MatDialog,
        private auth0Service: Auth0Service,
        private apiService: ApiService,
        private zitiLoginService: ZitiLoginService,
        private featureService: FeatureService,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.checkIsVanityUrl();

        this.jwtHelper = new JwtHelperService();
        if (window.location.hostname !== this.environment.originDomain) {
            this.isRootDomain = false;
        }

        this.auth0Service?.isAuthenticated$?.subscribe((isLoggedIn) => {
            this.isLoggedIn = isLoggedIn;
            // Clear on successful re-auth so a subsequent true session-expiry
            // within the same app instance still surfaces a growler.
            if (isLoggedIn) {
                this.logoutInProgress = false;
                this.armTokenRenewalOnBootstrap();
            } else {
                this.renewalArmed = false;
            }
            this.authenticationChangedSource.next(this.isLoggedIn);
            const wasLoggedIn = localStorage.getItem('wasLoggedIn') === 'true';
            if (this.isLoggedIn && this.isVanityUrl) {
                localStorage.setItem('wasLoggedIn', 'true');
                localStorage.setItem('isVanityUrl', 'true');
            }
            if (!this.isLoggedIn && wasLoggedIn) {
                localStorage.removeItem('wasLoggedIn');
                this.handleVanityUrl();
            }
        });

        // Unrecoverable Auth0 SDK errors (e.g. Missing Refresh Token from AuthHttpInterceptor's
        // silent refresh) previously only logged. Surface them as a clean logout so the user
        // doesn't get stuck on a page with silently failing API calls.
        this.auth0Service?.getErrorObservable()?.subscribe((error) => {
            if (!error) return;
            // Suppress errors that are a natural consequence of a user-initiated logout.
            if (this.logoutInProgress) return;
            const code = error.error || error.code || '';
            const message = error.message || '';
            const isUnrecoverable =
                code === 'missing_refresh_token' ||
                code === 'login_required' ||
                code === 'invalid_grant' ||
                code === 'consent_required' ||
                /missing refresh token/i.test(message) ||
                /login required/i.test(message);

            if (isUnrecoverable && this.isLoggedIn) {
                this.logger.error('Auth0 unrecoverable error, logging out', error);
                this.growlService.show(
                    new GrowlerData(
                        'error',
                        'Error',
                        'Session Expired',
                        'Your session has expired. Please log in again.'
                    )
                );
                this.logout(false);
            }
        });

        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
              this.url = event.url;
            }
        });

        this.apiService.currentRealm.subscribe((currentRealm) => {
          this.checkIsVanityUrl();
          this.currentRealm = currentRealm;
        });
    }

    /**
     * Call as soon as the user has committed to logging out (e.g. after
     * confirming the logout dialog) — before any async teardown (delays,
     * dialogs, auth0 sign-out) that could fire unrecoverable errors.
     * Prevents the Auth0 error-observable subscription from showing a
     * "Session Expired" growler for errors that are a natural consequence
     * of the user-initiated logout.
     */
    public willLogout(): void {
        this.logoutInProgress = true;
    }

    public logout(navigateToNotFound, skipTokenRevoke?: boolean, params?: any, localonly?: boolean, redirectUrl?: string): void {
        this.logoutInProgress = true;
        this.errorHistoryService.clearErrorHistory();

        this.menuService.unsetArea();

        this.authorizationService.obtainedMopPermissions = false;

        this.unscheduleRenewal();

        // it is possible the user has a dialog still open if they are being auto logged out
        // in this case we want to be sure that all dialogs are closed, as otherwise they would remain open after the user logs out
        for (const dialog of this.matDialog.openDialogs) {
            // when closing each dialog, pass an object containing a property loggingOut which is set to true
            // this is done as many of the dialogs in the nfconsole are set to preform some action that makes an API call after the dialog is closed
            // if the dialogs attempt this when the user is being logged out, they will be unauthenticated by the time the API call is made
            if (localonly) {
                dialog.close({ loggingOut: true });
            }
        }
        if (this.isAuthenticated() && !skipTokenRevoke) {
            this.iamService.logOut().subscribe(
                (result) => {
                    this.logger.info("Successfully revoked the user's token");
                },
                (error) => {
                    this.logger.info('Could not revoke the token due to an error: ', error);
                }
            );
        }

        this.checkIsVanityUrl();

        let returnTo = window.location.origin + '/login';
        if (this.isVanityUrl) {
            returnTo = window.location.origin + '/vanity-logout';
        }
        const logoutOptions: any = {
            logoutParams: {
                returnTo: returnTo,
            },
        };
        if (localonly) {
            // auth0-spa-js v2 removed the `localOnly` option; `openUrl: false` clears the local Auth0
            // session WITHOUT redirecting to Auth0's /v2/logout endpoint. Without this, the redirect
            // round-trips and lands on `returnTo` (/login), overriding the router.navigate below — which
            // is what bounced a failed invitation acceptance off the invitation screen to /login (MOP-27516).
            logoutOptions.openUrl = false;
        }

        this.clearStorage();

        if (localonly) {
          this.brandingService.restore();
        }

        this.auth0Service.logout(logoutOptions);
        // if navigateToNotFound is true, navigate the user to the PageNotFound component after logging out
        if (navigateToNotFound) {
            this.router.navigate(['/page-not-found']);
        } else if (localonly) {
            // otherwise, navigate to the login page (or a caller-specified destination, e.g. the
            // invitation review screen when a rejected acceptance should land back on the invite).
            this.router.navigate([redirectUrl || '/login'], params);
        }
    }

    public isAuthenticated(): boolean {
        return this.isLoggedIn;
    }

    /**
     * Whether the page on screen is one a signed-out visitor is meant to reach.
     *
     * The list is here rather than read off the route's `skipAuth` data because
     * {@link handleVanityUrl} runs before the router resolves anything. A public page missing from
     * this list still loads on the bare domain and still bounces to login on an organization's
     * vanity host, which is the only host a shared link ever names.
     */
    public pageIsUnauthenticated(): boolean {
      if (
        (this.url.indexOf(URLS.INVITATION) >= 0 && this.url.indexOf(URLS.MANAGE_INVITATIONS)) <= -1 ||
        this.url.indexOf(URLS.STORED_SECRETS) >= 0 && this.url.indexOf('?token=') >= 0 ||
        this.url.indexOf(URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR) >= 0 ||
        this.url.indexOf(URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR_V2) >= 0 ||
        this.url.indexOf(URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR_V3) >= 0 ||
        this.url.indexOf(URLS.FRONTDOOR_CLIENT_CERTIFICATES_RENEW) >= 0 ||
        window.location.pathname === URLS.INVITATION ||
        window.location.pathname.indexOf(URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR) >= 0 ||
                window.location.pathname.indexOf(URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR_V2) >= 0 ||
                window.location.pathname.indexOf(URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR_V3) >= 0 ||
                window.location.pathname.indexOf(URLS.FRONTDOOR_CLIENT_CERTIFICATES_RENEW) >= 0
      ) {
        return true;
      }
      return false;
    }

    /**
     * If we are here, that means we are on the vanity URL and we need to login through the root domain, so redirect there and pass the originating Organization name
     * - Vanity URL Step #2
     */
    public _redirectToRootDomainForLogin(hostname) {
        // capture the Organization name from the URL
        this.logger.info('VANITY - We are on the vanity URL');
        const subdomainStr = hostname.replace(this.environment.originDomain, '');
        let subdomains = subdomainStr.split('.');
        subdomains = subdomains.filter((subdomain) => {
          return !isEmpty(subdomain);
        });
        let realmLabel, orgLabel;
        if (subdomains.length === 2) {
          realmLabel = subdomains[1];
          orgLabel= subdomains[0];
          if (this.environment.realmsSupported) {
            this._checkForRealm(realmLabel, orgLabel);
          } else {
            this._handleVanityUrlNoRealm(orgLabel);
          }
        } else if (subdomains.length === 1) {
          realmLabel = subdomains[0];
          if (this.environment.realmsSupported) {
            this._checkForRealm(realmLabel, orgLabel);
          } else {
            this._handleVanityUrlNoRealm(realmLabel);
          }
        } else {
          window.location.href = this.environment.identityConfig.loginReturnUrl;
          return;
        }
    }

    _checkForRealm(realmLabel, orgLabel) {
      lastValueFrom(this.iamService.getRealmByLabel(realmLabel, true)).then((realmData: any) => {
        if (isEmpty(realmData?.id)) {
          this._handleVanityUrlNoRealm(realmLabel);
        } else {
          this.isRealmLogin = true;
          if (isEmpty(orgLabel)) {
            this._handleRealmLoginNoVanity(realmData);
          } else {
            this._handleRealmLoginWithVanity(realmData, orgLabel);
          }
        }
      }).catch((error) => {
        this.logger.error('Failed to check for realm data for site name: ' + realmLabel + '. Falling back to vanity URL check.');
        this._handleVanityUrlNoRealm(realmLabel);
      })
    }

    _handleRealmLoginNoVanity(realmData) {
      this.isVanityUrl = false;
      this.brandingService.loadBrandingStylesheets(realmData.id, undefined).then((result) => {
        this.router.navigate(['/login']);
      });
    }

    _handleRealmLoginWithVanity(realmLabel, orgLabel) {
      this.vanityLogin = true;
      this.tenantLabel = orgLabel;
      this.getIdentityProviders();
    }

    _handleVanityUrlNoRealm(orgLabel) {
      let url = location.protocol + '//' + this.environment.originDomain;
      if (this.environment['originPort'] !== undefined && this.environment['originPort']) {
        url = url + ':' + this.environment.originPort;
      }

      // add siteName to path
      url = url + '/login?loginSiteName=' + orgLabel;
      this.logger.info('Callback URL being passed to Auth0 is: ' + this.getCallbackUrl(orgLabel));
      this.logger.info('Navigating to: ' + url);
      // This redirects bck to the root domain with the 'loginSiteName' being passed as a GET param, see step #3 above
      this._catchVanityUrlAuth(orgLabel);
    }

    /**
     * Handle the incoming auth result that was passed to us from the root domain
     * This happens if the user has just logged in through the root domain, the Auth0 result is passed over the URL to us
     * Need to decode it and save it in our local storage to it matches the vanity domain's domain.
     *
     * - Vanity URL Step #6 (continued)
     */
    public _catchAuthResult() {
        this.logger.info('Catch the incoming AuthResult for the vanity domain (_catchAuthResult() function )');
        const urlParams = new URLSearchParams(window.location.search);
        let authResult = urlParams.get('authResult');
        authResult = atob(authResult);
        // this.logger.info('Raws JSON String AuthResult', authResult);
        try {
            authResult = JSON.parse(authResult);
        } catch (err) {
            this.logger.error('Error decoding the auth result from the URL. JSON parse failure', err);
            this.growlService.show(
                new GrowlerData(
                    'error',
                    'Error',
                    'Authentication Error',
                    'There was a problem authenticating, please try again'
                )
            );
            this.router.navigate(['/login']);
            return;
        }

        this._setSession(authResult);
        this.logger.info('Should be logged into vanity URL, redirect to dashboard');
        const url = this.featureService.isCloudZiti ? '/dashboard' : '/v7/dashboard';
        this.apiService.resetInitialNetworkId();
        this.router.navigate([url]);
    }

    public clearStorage() {
        localStorage.removeItem('access_token');
        localStorage.removeItem('id_token');
        localStorage.removeItem('token');
        localStorage.removeItem('expires_at');
        localStorage.removeItem('organizationId');
        localStorage.removeItem('profile_name');
        localStorage.removeItem('proflie_nick');
        localStorage.removeItem('profile_picture');
        localStorage.removeItem('profile_email');
        localStorage.removeItem('tenantName');
        localStorage.removeItem('tenantLabel');
        localStorage.removeItem('tenantId');
        localStorage.removeItem('isVanityUrl');
        localStorage.removeItem('wasLoggedIn');
        sessionStorage.removeItem('growlerErrors');
        sessionStorage.removeItem('lastTenantId');
    }

    /**
     * Save the Auth0 Result to Local Storage for the current domain
     */
    public _setSession(authResult): void {
        // Set the time that the access token will expire at
        this.logger.info('Setting session');
        this.logger.info('AUTH RESULT', authResult);
        const expiresAt = JSON.stringify(authResult.expiresIn * 1000 + new Date().getTime());
        const jwt = this.jwtHelper.decodeToken(authResult.accessToken);
        // fetch and store the Tenant info
        // Need to call this before the access token is set otherwise we will get a 403 (API bug)
        this.iamService.getOrgByShortName(jwt['https://netfoundry.io/tenant/label']).subscribe((result: any) => {
            this.logger.info('Tenant Info', result);
            localStorage.setItem('tenantName', result.name);
            localStorage.setItem('tenantLabel', result.label);
            localStorage.setItem('tenantId', result.id);
            localStorage.setItem('loggedInTenantId', result.id);

            // set branding once we have the tenant label
            const html = document.getElementsByTagName('body')[0];

            if (!this.environment.realmsSupported) {
              this.brandingService.fetchBrandCss().then((rawCss) => {
                html.style.cssText = rawCss;
              });
            }

            // checking if the tenant the user is logged into is the same as the last tenant that was logged into
            //  in this session
            if (result.id !== sessionStorage.getItem('lastTenantId')) {
                // if the tenants are different, remove the old growler messages
                sessionStorage.removeItem('growlerErrors');
            }
            sessionStorage.setItem('lastTenantId', result.id);
        });

        localStorage.setItem('access_token', authResult.accessToken);
        localStorage.setItem('expires_at', expiresAt);
        localStorage.setItem('Auth0Id', jwt.sub);
        localStorage.setItem('connectionId', jwt['https://netfoundry.io/auth0/connectionId']);

        this.scheduleRenewal();
    }

    initiateSiteNameRequest() {
        this.errorEmailAddress = false;
        if (this.validateService.isValidEmail(this.emailAddress)) {
            this.emailAddress = this.emailAddress.trim();

            this.iamService.sendForgottenSitenames(this.emailAddress).subscribe((result) => {
                this.forgotSiteName = false;
                this.growlService.show(
                    new GrowlerData(
                        'success',
                        'Success',
                        'Email Sent',
                        'An email with your organization names has been sent to the address provided'
                    )
                );
            });
        } else {
            this.errorEmailAddress = true;
            this.growlService.show(
                new GrowlerData('error', 'Error', 'Invalid Email Address', 'Please enter a valid email address.')
            );
        }
    }

    // Re-arm renewal for a session restored on reload. Skips the /callback and /login routes:
    // _setSession handles a fresh login there, and refreshing mid-callback logs the user out.
    private armTokenRenewalOnBootstrap(): void {
        if (this.renewalArmed || this.logoutInProgress) {
            return;
        }
        const path = window.location.pathname || '';
        if (path.indexOf(URLS.CALLBACK) >= 0 || path.indexOf('/login') >= 0) {
            return;
        }
        // Absent until _setSession runs, so its presence means an established (reloaded) session.
        const token = localStorage.getItem('access_token');
        if (!token) {
            return;
        }
        let exp: Date | null = null;
        try {
            exp = this.jwtHelper.getTokenExpirationDate(token);
        } catch {
            exp = null;
        }
        if (!exp) {
            return;
        }
        this.renewalArmed = true;
        // Keep expires_at in sync so the renewal timer fires on time, not immediately.
        localStorage.setItem('expires_at', JSON.stringify(exp.getTime()));
        if (exp.getTime() - Date.now() <= 120000) {
            this.renewToken();
        } else {
            this.scheduleRenewal();
        }
    }

    // Silent refresh + persist; side-effect free (no growler/logout) so callers decide on failure.
    public async refreshAccessToken(): Promise<boolean> {
        try {
            // Cap the wait so a stalled refresh fails instead of hanging the route resolver.
            const accessToken = await lastValueFrom(
                this.auth0Service.getAccessTokenSilently({ cacheMode: 'off' }).pipe(timeout(15000))
            );
            if (!accessToken) {
                return false;
            }
            const exp = this.jwtHelper.getTokenExpirationDate(accessToken);
            if (!exp) {
                return false;
            }
            localStorage.setItem('access_token', accessToken);
            localStorage.setItem('expires_at', JSON.stringify(exp.getTime()));
            this.scheduleRenewal();
            return true;
        } catch (err) {
            this.logger.error('refreshAccessToken failed', err);
            return false;
        }
    }

    // function for requesting a fresh access token for the user
    public renewToken() {
        // Don't attempt renewal once the user has initiated logout — the call
        // would fail and incorrectly show a "Session Expired" growler.
        if (this.logoutInProgress) {
            return;
        }
        this.logger.info("Attempting to renew user's token");

        // NetFoundry-side bookkeeping (nfToken); independent of Auth0 renewal below.
        this.iamService.refreshToken().subscribe({
            next: (result) => {
                if (result?.['nfToken'] === undefined) {
                    this.logger.info('failed to get nfToken from authorize-refresh');
                }
            },
            error: (err) => this.logger.warn('authorize-refresh failed', err),
        });

        // cacheMode: 'off' forces the SDK to use the refresh_token grant instead of returning a cached token.
        this.auth0Service.getAccessTokenSilently({ cacheMode: 'off' }).subscribe({
            next: (accessToken: string) => {
                if (!accessToken) {
                    this.logger.error('Silent renewal returned empty token');
                    return;
                }
                const exp = this.jwtHelper.getTokenExpirationDate(accessToken);
                if (!exp) {
                    this.logger.error('Renewed access token had no exp claim');
                    return;
                }
                localStorage.setItem('access_token', accessToken);
                localStorage.setItem('expires_at', JSON.stringify(exp.getTime()));
                this.logger.info('Silent renewal succeeded; rescheduling');
                this.scheduleRenewal();
            },
            error: (err) => {
                this.logger.error('Silent token renewal failed', err);
                // If logout has started between scheduling and this callback, skip
                // the growler — the error is expected during teardown.
                if (this.logoutInProgress) {
                    return;
                }

                this.growlService.show(
                    new GrowlerData(
                        'error',
                        'Error',
                        'Session Expired',
                        'Unable to renew your session. Please log in again.'
                    )
                );
                this.logout(false);
            },
        });
    }

    // function for scheduling the renewal of the token
    // the time it takes to renew the token is based off the current time and the time the token expires
    // as such, it is OK to call this function multiple times before the token is set to expire as the time until renewal
    // will never increase
    public scheduleRenewal() {
        // making sure the user is authenticated before proceeding
        // if the user is no longer authenticated then it won't be possible to renew their token
        if (!this.isAuthenticated()) {
            // displaying a growler
            this.growlService.show(
                new GrowlerData(
                    'error',
                    'Error',
                    'Unable to Refresh Session',
                    'Unable to refresh the current session. Please log out and log back in to restart your session'
                )
            );

            // logging the user out
            this.logout(false);
        }

        // getting rid of any previous renewal subscriptions
        this.unscheduleRenewal();

        this.refreshSub = new Subscription();

        // getting expired_at from localStorage
        const expiresAt = JSON.parse(localStorage.getItem('expires_at'));

        // creating an observable timer based on expired at
        const expiresIn$ = of(expiresAt).pipe(
            mergeMap((expiresAt: number) => {
                const now = Date.now();
                // Use timer to track delay until expiration
                // to run the refresh at the proper time
                // this timer will end 2 seconds before the token is set to expire
                // this is necessary as it is possible for isAuthenticated to fail if the timer ends too
                // close to the expiration of the token
                const result = timer(Math.max(2000, expiresAt - (now + 2000)));
                return result;
            })
        );

        // Once the delay time from above is
        // reached, get a new JWT and schedule
        // additional refreshes
        this.refreshSub = expiresIn$.subscribe(() => {
            // renewing the token
            this.renewToken();
        });
    }

    // function for unscheduling the token renewal
    public unscheduleRenewal() {
        // unsubscribing the refreshSub. this should not only unsubscribe from but also kill the Observable timer
        this.refreshSub.unsubscribe();
    }

    returnToVanityLogin() {
        this.router.navigate(['/login'], { queryParams: {} }).then((result) => {
            this.handleVanityUrl();
        });
    }

    returnToLogin() {
      const origin = window.location.origin;
      window.location.href = origin + '/login';
    }

    /**
     * This is the handler that determine what to do about vanity URLs for every page load
     * - Vanity URL Step #1
     */
    public handleVanityUrl() {
        // not worried if we're already authenticated
        if (this.isAuthenticated() || this.pageIsUnauthenticated()) {
            return;
        }

        // Are we trying to process the incoming authResult right now?
        // Vanity URL step #6
        if (this.locationService.path().match(/authResult/)) {
            this._catchAuthResult();
            return;
        }
        this.checkIsVanityUrl();
        const hostname = window.location.hostname;
        if (this.currentRealm?.label && (this.currentRealm.label !== 'netfoundry' || this.rootSubDomain === 'netfoundry')) {
          this.isRealmLogin = true;
        }
        const wasLoggedIn = !isEmpty(localStorage.getItem('wasLoggedIn'));
        if (
            this.isVanityUrl &&
            (wasLoggedIn ||
                this.locationService.path().match(/callback\?code=/) ||
                this.locationService.path().match(/login\?authToken=/))
        ) {
            return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const isVanityLogout =
            urlParams.get('vanityLogout') === 'true' || window.location.pathname?.indexOf('vanity-logout') >= 0;
        // Check if the incoming domain is the root domain, if it's not, handle the redirect
        if (this.isVanityUrl && !isVanityLogout) {
            this.isResuming = true;
            this._redirectToRootDomainForLogin(hostname);
        }
    }

    checkIsVanityUrl() {
      const hostname = window.location.hostname;
      if (this.environment.realmsSupported && this.currentRealm?.id && hostname !== this.environment.originDomain) {
        const domains = hostname?.split('.') || [];
        this.isVanityUrl = domains[0] !== this.currentRealm.label;
        this.rootSubDomain = domains[0];
      } else {
        this.isVanityUrl = hostname !== this.environment.originDomain;
      }
      return this.isVanityUrl;
    }
    /**
     * Add a flag to the callback URL that we pass to Auth0 so it has the "returnToSiteName" GET parameter in the URL
     */
    updateCallbackUrl(siteName) {
        this.options.auth.redirectUrl = this.getCallbackUrl(siteName);
    }

    /**
     * What's the magic callback URL that let's us know what vanity domain to go to once we're authorized
     */
    getCallbackUrl(siteName) {
        const url = this.environment.authconfig.callbackURL + '?returnToSiteName=' + siteName;
        this.logger.info('Callback URL for auth0 is: ' + url);
        return url;
    }

    /**
     * We have a valid Auth Result from Auth0. Package up the result and pass it in the URL to the root domain
     * We have a valid Auth Result from Auth0. Package up the result and pass it in the URL to the root domain
     *  - Vanity URL step #5
     */
    goToVanitySite(authResult) {
        // extract the sitename param
        const urlParams = new URLSearchParams(window.location.search);
        const siteName = urlParams.get('returnToSiteName');

        let url = '';
        if (this.environment.domain === 'localhost' || this.environment.domain === 'uuix') {
            url = `${location.protocol}//${siteName}.${this.environment.domain}:${this.environment.originPort}/callback`;
        } else {
            url = `${location.protocol}//${siteName}.${this.environment.originDomain}/callback`;
        }

        // Attempt to shrink the authResult into only the necessary parts, otherwise the GET param is too large
        const authResultSmall = {
            expiresIn: authResult.expiresIn,
            accessToken: authResult.accessToken,
        };

        url = url + '?authResult=' + btoa(JSON.stringify(authResultSmall));
        this.logger.info('GO TO VANITY URL NOW: ' + url);
        window.location.href = url;
    }

    public continue(state) {
        window.location.href = `https://${this.environment.authconfig.domain}/continue?state=${state}`;
    }

    public acceptInvite(nfToken, connection, prefill, showLogin = true) {
        this.options.prefill.email = prefill;
        this.options.closable = showLogin;
        // create a new lock otherwise the prefills won't show

        this.logger.info('Allowed Provider Connection', connection);

        // let initialScreen = 'signUp';
        // if (connection.auth0ConnectionType.toLowerCase() === 'enterprise') {
        //     initialScreen = 'login';
        // }
        localStorage.setItem(`showConsoleTour_${prefill}`, 'true');
    }

    // Updated Auth Flow
    public async newLogin(nfToken, returnUrl, connections) {
        const idps = [];
        this.identityProviders?.forEach((idp) => {
            idps.push({ connectionId: idp.auth0ConnectionId, name: idp.name });
        });

        this.checkIsVanityUrl();
        const redirectUri = window.location.origin + '/callback';  //this.environment.authconfig.callbackURL;
        if ((this.environment.alwaysVanityUrl || this.environment.realmsSupported) && !this.isVanityUrl) {
            let newUri = '';
            if (window.location.href.indexOf('https://') >= 0) {
                const redirectLocation = window.location.href.slice(8);
                newUri = `https://${this.tenantLabel}.${redirectLocation}`;
            } else {
                const redirectLocation = window.location.href.slice(7);
                newUri = `http://${this.tenantLabel}.${redirectLocation}`;
            }
            window.location.href = newUri;
            return Promise.resolve();
        }

        this.zitiLoginService.clearZitiSession();
        const url = this.featureService.isCloudZiti ? '/dashboard' : '/v7/dashboard';
        const appState = {
            nfToken: nfToken,
            target: url,
            allowSignup: false,
            identityProviders: this.identityProviders,
            branding: null,
        };

        // These values must be sent as query params so the Auth0 hosted login page can read them.
        // Classic ULP reads the bare `config.extraParams.*`; ACUL only surfaces custom params
        // prefixed `ext-` (untrustedData.authorizationParams). One build serves both, so emit both.
        const idpsJson = JSON.stringify(idps);
        const authorizationParams: any = {
            nfToken: nfToken,
            allowSignup: 'false',
            identityProviders: idpsJson,
            // ext- value must be pattern-safe: space-separated connection ids (no JSON).
            'ext-idps': idps.map((i) => i.connectionId).join(' '),
            // cloud-ziti shares nfconsole's Auth0 client; this hints the ACUL brand/title.
            'ext-app': this.featureService.isCloudZiti ? 'cloudziti' : undefined,
            // Theme is NOT the user's preference here: the ACUL login uses the org's default theme,
            // which the bundle reads from ext-brand.theme (sent below for realm-enabled apps).
            tenantLabel: this.tenantLabel,
            redirect_uri: redirectUri,
        };

        // New Universal Login (ACUL): the base theme is resolved server-side, but realms are a
        // NetFoundry construct (not Auth0 orgs), so pass the realm's colors + logo for the ACUL
        // bundle to apply. The bundle validates every value (the param is attacker-influenceable).
        if ((this.environment.authconfig as { newUniversalLogin?: boolean }).newUniversalLogin) {
            if (this.environment.realmsSupported) {
                try {
                    // Bound the branding fetch so a slow/hung call can't stall the login redirect.
                    const rb: any = await lastValueFrom(
                        from(
                            this.brandingService.loadBrandingStylesheets(
                                this.loginOrganization?.realmId,
                                this.loginOrganization?.id
                            )
                        ).pipe(timeout(2000))
                    );
                    // Fill empty org fields from realm defaults, then send structured branding.
                    // base64url the colors blob and each logo URL separately so every ext- value is
                    // pattern-safe and under Auth0's 255-char ext-param limit.
                    const resolved: any = this.brandingService.resolveBrandingWithRealmFallbacks(rb);
                    authorizationParams['ext-brand'] = toB64Url(JSON.stringify({
                        primaryColor: resolved.primaryColor,
                        secondaryColor: resolved.secondaryColor,
                        darkPrimaryColor: resolved.darkPrimaryColor,
                        darkSecondaryColor: resolved.darkSecondaryColor,
                        theme: resolved.theme,
                    }));
                    if (resolved.lightLogo) authorizationParams['ext-logol'] = toB64Url(String(resolved.lightLogo));
                    if (resolved.darkLogo) authorizationParams['ext-logod'] = toB64Url(String(resolved.darkLogo));
                } catch (e) {
                    this.logger.info('ACUL realm branding fetch failed/timed out; using app default brand', e);
                }
            }
            this.auth0Service.loginWithRedirect({ appState, authorizationParams });
            return;
        }

        if (this.brandingService.defaultBrandCSS) {
            authorizationParams.branding = this.brandingService.defaultBrandCSS;
        }
        if (this.environment.realmsSupported) {
          await this.brandingService
            .getOrgStyleBlock(this.loginOrganization?.realmId, this.loginOrganization?.id)
            .then((rawCss) => {
              authorizationParams.branding = rawCss;
            })
            .finally(() => {
              return this.auth0Service.loginWithRedirect({
                  appState: appState,
                  authorizationParams: authorizationParams,
              });
            });
          return;
        }
        await this.brandingService
            .fetchBrandCss(this.tenantLabel)
            .then((rawCss) => {
                authorizationParams.branding = rawCss;
            })
            .finally(() => {
                authorizationParams.branding = (authorizationParams.branding || '') + this.brandingService.loginBrandingCSS;
                return this.auth0Service.loginWithRedirect({
                    appState: appState,
                    authorizationParams: authorizationParams,
                });
            });
    }

    // Call this method in app.component.ts
    // if using hash-based routing
    public handleAuthenticationWithHash(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationStart),
                filter((event: NavigationStart) => /access_token|id_token|error(?!-history)/.test(event.url))
            )
            .subscribe(() => {
                this.logger.info('Resuming Auth from Auth0');
                this.isResuming = true;
                this._resumeAuthFromHash();
            });
    }

    async getIdentityProviders() {
        if (!this.tenantLabel || this.tenantLabel === '') {
            this.errorSiteName = true;
            this.growlService.show(
                new GrowlerData(
                    'error',
                    'Error',
                    'Invalid Organization Name',
                    'Please enter a valid organization name.'
                )
            );
        }

        this.identityProviders = [];
        await this.iamService
            .getOrgByShortName(this.tenantLabel)
            .toPromise()
            .then(
                (organization) => {
                    this.identityProviders = organization['identityProviders'];
                    this.loginOrganization = organization;
                },
                (error) => {
                    if (error.status === 404) {
                        this.errorSiteName = true;
                        this.growlService.show(
                            new GrowlerData(
                                'error',
                                'Error',
                                'Invalid Organization Name',
                                'No organization exists with this name.'
                            )
                        );
                    } else {
                        this.errorSiteName = true;
                        this.growlService.show(
                            new GrowlerData(
                                'error',
                                'Error',
                                'Invalid Organization Name',
                                'Please enter a valid organization name.'
                            )
                        );
                    }
                }
            );
        this.initiateLogin(this.identityProviders[0]);
    }

    // Updated Auth Flow
    async initiateLogin(identityProvider) {
        this.iamService.authorizeInitiate(this.tenantLabel).subscribe(
            (result: any) => {
                this.errorSiteName = false;
                this.logger.info('Authorize tenant label: ' + this.tenantLabel, result);

                // Pop error if no token came back
                if (result['nfToken'] === undefined) {
                    this.vanityLogin = false;
                    this.isResuming = false;
                    this.growlService.show(
                        new GrowlerData(
                            'error',
                            'Error',
                            'Invalid Organization Name',
                            'No nfToken came back from the API. See the console for more details'
                        )
                    );
                } else {
                    this.newLogin(result.nfToken, result.intermediateReturnUrl, [identityProvider.auth0ConnectionId]);
                }
            },
            (error) => {
                if (error.status === 400) {
                    this.errorSiteName = true;
                    this.vanityLogin = false;
                    this.isResuming = false;
                    this.growlService.show(
                        new GrowlerData(
                            'error',
                            'Error',
                            'Invalid Organization Name',
                            'The organization name provided does not exist.'
                        )
                    );
                    // If org/vanity login is invalid, wait a few seconds after showing growler and then redirect to root domain/login
                    delay(() => {
                        if (this.isVanityUrl) {
                            window.location.href = this.environment.identityConfig.loginReturnUrl;
                        }
                    }, 6000);
                }
            }
        );
    }

    /**
     * A redirect has landed here on the root domain from a vanity URL
     *  - Catch the siteName from the URL parameters,
     *  - Hide the login form
     *  - Determine the Auth0 callback URL
     *  - Rebuild the Auth0 Lock object just to make sure there aren't memory artifacts
     *  - Pop the Auth0 lock and do the standard login
     *
     *  - Vanity URL Step #3 (continued)
     */
    public _catchVanityUrlAuth(siteName) {
        this.logger.info('Handling auth incoming auth request for a vanity URL - catchVanityUrlAuth');
        this.vanityLogin = true;
        this.tenantLabel = siteName;
        this.updateCallbackUrl(siteName);
        if (this.isAwsLogin) {
            this.dialogRef = this.dialogForm.open(AwsSplashScreenComponent, {
                data: {},
                minHeight: '100%',
                minWidth: '100%',
                height: '100%',
                width: '100%',
            });
            this.dialogRef.afterClosed().subscribe(() => {
                this.getIdentityProviders();
            });
        } else {
            this.getIdentityProviders();
        }
    }

    /**
     * The lock intercepts the auth info from the URL's hash (everything after the # sign) and then the helper clears it from the URL
     * This must be done from the root domain otherwise it pukes because the state nonce does not match
     * For vanity URLs, we have to fully capture and decode the token, and then forward that to the vanity domain to save into that domain's local storage
     */
    private _resumeAuthFromHash() {}
}
